import { useEffect, useState } from 'react'
import type { AiPurpose } from '@cms/contracts'
import { listAiAccountsFor, type AiAccountList } from '../api/ai.js'
import { accountOptions } from '../domain/ai-generation.js'
import { useI18n } from '../i18n/context.js'
import { SelectField } from '../ui/fields.js'

/**
 * Which AI subscription answers (document/42 §3.1). An account that cannot be
 * used right now stays in the list, disabled, with the reason AI gave, instead
 * of vanishing. When AI cannot even be asked the list is empty and the
 * default account is used: generating is never blocked by a dropdown.
 */
export function AiAccountSelect({ purpose, value, onChange, disabled }: {
  purpose: AiPurpose
  value: string
  onChange: (accountId: string) => void
  disabled?: boolean
}) {
  const { t } = useI18n()
  const [list, setList] = useState<AiAccountList | null>(null)

  useEffect(() => {
    let active = true
    listAiAccountsFor(purpose)
      .then((result) => { if (active) setList(result) })
      .catch(() => { if (active) setList({ accounts: [], degraded: true }) })
    return () => { active = false }
  }, [purpose])

  const options = accountOptions(list?.accounts ?? [])
  const chosen = options.find((option) => option.id === value)
  return <SelectField
    label={t('aiGen.common.account')}
    value={value}
    disabled={disabled || list === null}
    supporting={list?.degraded ? t('aiGen.common.accountsDegraded') : chosen?.disabled && chosen.reason ? chosen.reason : t('aiGen.common.accountHelp')}
    onChange={(event) => onChange(event.target.value)}
  >
    <option value="">{t('aiGen.common.accountDefault')}</option>
    {options.map((option) => <option key={option.id} value={option.id} disabled={option.disabled}>
      {option.disabled && option.reason ? t('aiGen.common.accountUnavailable', { label: option.label, reason: option.reason }) : option.label}
    </option>)}
  </SelectField>
}
